import React from "react";
import MaskedInput from "react-text-mask";
import { ISubAccountFormValues } from "./types";

interface SubAccountFormFieldsProps {
  values: ISubAccountFormValues;
  errors: Partial<Record<keyof ISubAccountFormValues, string>>;
  mode: "create" | "edit";
  lockIdentityFields?: boolean;
  disabled?: boolean;
  onChange: <K extends keyof ISubAccountFormValues>(
    key: K,
    value: ISubAccountFormValues[K]
  ) => void;
}

const phoneMask = [
  "(",
  /[1-9]/,
  /\d/,
  ")",
  " ",
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  "-",
  /\d/,
  /\d/,
  /\d/,
  /\d/,
];

const inputClassName =
  "w-full rounded-xl border border-[#E2E8F0] px-3 py-2.5 text-sm text-[#2D3748] outline-none focus:border-[#DD6435] focus:ring-2 focus:ring-[#DD6435]/20 disabled:cursor-not-allowed disabled:bg-[#F8FAFC] disabled:text-[#94A3B8]";

export default function SubAccountFormFields({
  values,
  errors,
  mode,
  lockIdentityFields = false,
  disabled = false,
  onChange,
}: SubAccountFormFieldsProps) {
  const identityDisabled = disabled || lockIdentityFields;

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {lockIdentityFields && (
        <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 md:col-span-2">
          Esta conta já existia no sistema. E-mail, telefone e senha não podem
          ser alterados — apenas status e imóveis.
        </p>
      )}

      <label className="block space-y-1.5">
        <span className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">
          Nome
        </span>
        <input
          type="text"
          value={values.first_name}
          disabled={disabled || lockIdentityFields}
          onChange={(event) => onChange("first_name", event.target.value)}
          className={inputClassName}
        />
        {errors.first_name && (
          <span className="text-xs text-[#E53E3E]">{errors.first_name}</span>
        )}
      </label>

      <label className="block space-y-1.5">
        <span className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">
          E-mail
        </span>
        <input
          type="email"
          value={values.email}
          disabled={identityDisabled}
          onChange={(event) => onChange("email", event.target.value)}
          className={inputClassName}
        />
        {errors.email && (
          <span className="text-xs text-[#E53E3E]">{errors.email}</span>
        )}
      </label>

      <label className="block space-y-1.5">
        <span className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">
          Telefone
        </span>
        <MaskedInput
          mask={phoneMask}
          guide={false}
          value={values.phone}
          disabled={identityDisabled}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            onChange("phone", event.target.value)
          }
          className={inputClassName}
        />
        {errors.phone && (
          <span className="text-xs text-[#E53E3E]">{errors.phone}</span>
        )}
      </label>

      <label className="flex items-center gap-3 self-end py-2.5">
        <input
          type="checkbox"
          className="h-4 w-4 accent-[#DD6435] disabled:cursor-not-allowed"
          checked={values.active}
          disabled={disabled}
          onChange={(event) => onChange("active", event.target.checked)}
        />
        <span className="text-sm font-medium text-[#2D3748]">Acesso ativo</span>
      </label>

      {!lockIdentityFields && (
        <>
          <label className="block space-y-1.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">
              Senha {mode === "edit" ? "(opcional)" : ""}
            </span>
            <input
              type="password"
              value={values.password}
              disabled={disabled}
              onChange={(event) => onChange("password", event.target.value)}
              className={inputClassName}
              autoComplete="new-password"
            />
            {errors.password && (
              <span className="text-xs text-[#E53E3E]">{errors.password}</span>
            )}
          </label>

          <label className="block space-y-1.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">
              Confirmar senha
            </span>
            <input
              type="password"
              value={values.password_confirmation}
              disabled={disabled}
              onChange={(event) =>
                onChange("password_confirmation", event.target.value)
              }
              className={inputClassName}
              autoComplete="new-password"
            />
            {errors.password_confirmation && (
              <span className="text-xs text-[#E53E3E]">
                {errors.password_confirmation}
              </span>
            )}
          </label>
        </>
      )}
    </div>
  );
}
